// utils/qocIpcafAssessmentExcel.js
// Excel export/import for IPCAF assessments.
//
// Format decision: each question row has a "Selected Keys" cell where the
// assessor types the option letter(s) — "b" for single-select, "a,c,e" for
// multi-select — validated on import against that question's real option
// set (matched by ref_no, not row position, so reordering rows is safe).
// A "Legend" column on every row spells out what each letter means and its
// point value, so the sheet is self-explanatory without a separate manual.
//
// One sheet per core component (CC1..CC8) so the layout mirrors the on-screen
// section tabs; a hidden "Meta" sheet embeds routing info (assessment_id,
// facility_id) so bulk import can auto-match files without a modal open,
// exactly like the existing clause-assessment offline workflow.

import ExcelJS from 'exceljs';

const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1890FF' } };
const HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' } };
const GROUP_FILL  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F5FF' } };

function buildLegend(question) {
    return question.options
        .map(o => `${o.key}=${o.label} (${o.score}pt${o.score === 1 ? '' : 's'})`)
        .join('\n');
}

// ─── Export ─────────────────────────────────────────────────────────────────
// sections: [{code, num, title}], questions: flat array with section_id/options,
// responses: { [question_id]: { selected_options, score, comment } }
export async function exportIpcafAssessmentExcel({ facility, assessment, sections, questions, responses, assessmentDate }) {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'QoC Platform';
    workbook.created = new Date();

    const sectionById = sections.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});
    const questionsBySection = questions.reduce((acc, q) => {
        const code = sectionById[q.section_id]?.code;
        if (!code) return acc;
        (acc[code] = acc[code] || []).push(q);
        return acc;
    }, {});

    // ── One sheet per section ──────────────────────────────────────────────
    sections.forEach(sec => {
        const sheet = workbook.addWorksheet(sec.code, {
            views: [{ state: 'frozen', ySplit: 1 }],
        });

        sheet.columns = [
            { header: 'Ref No',        key: 'ref_no',   width: 9 },
            { header: 'Question',      key: 'question', width: 55 },
            { header: 'Type',          key: 'type',      width: 10 },
            { header: 'Legend (letter = option, points)', key: 'legend', width: 50 },
            { header: 'Selected Keys', key: 'selected',  width: 18 },
            { header: 'Comment',       key: 'comment',   width: 30 },
            { header: 'Score',         key: 'score',     width: 8 },
        ];
        sheet.getRow(1).eachCell(cell => {
            cell.fill = HEADER_FILL;
            cell.font = HEADER_FONT;
        });

        const qs = questionsBySection[sec.code] || [];
        let lastGroup = null;
        qs.forEach(q => {
            if (q.group_label && q.group_label !== lastGroup) {
                const groupRow = sheet.addRow({ ref_no: '', question: `— ${q.group_label} —` });
                groupRow.eachCell(cell => { cell.fill = GROUP_FILL; cell.font = { italic: true }; });
                lastGroup = q.group_label;
            } else if (!q.group_label) {
                lastGroup = null;
            }

            const r = responses[q.id] || {};
            const row = sheet.addRow({
                ref_no:   q.ref_no,
                question: q.question_text,
                type:     q.question_type,
                legend:   buildLegend(q),
                selected: (r.selected_options || []).join(','),
                comment:  r.comment || '',
                score:    r.score || 0,
            });
            row.getCell('legend').alignment = { wrapText: true };
            row.getCell('question').alignment = { wrapText: true };
            row.getCell('ref_no').numFmt = '@'; // force text, so "1.10" doesn't become a date/number
        });
    });

    // ── Hidden Meta sheet for auto-routing on bulk import ──────────────────
    const meta = workbook.addWorksheet('Meta');
    meta.state = 'veryHidden';
    meta.addRows([
        ['key', 'value'],
        ['ipcaf_assessment_id', assessment?.id || ''],
        ['facility_id',         facility?.id || ''],
        ['facility_name',       facility?.business_name || ''],
        ['assessment_date',     assessmentDate || ''],
        ['exported_at',         new Date().toISOString()],
        ['ipcaf_schema_version', 1],
    ]);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const filename = `${(facility?.business_name || 'Facility').replace(/[^\w\- ]/g, '')}_IPCAF_${assessmentDate}.xlsx`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return filename;
}

// ─── Import ─────────────────────────────────────────────────────────────────
// questions: the same flat array (with .options) used for export, so keys
// typed in the sheet can be validated against the real option set per ref_no.
export async function importIpcafAssessmentExcel(file, questions) {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    const questionByRefNo = questions.reduce((acc, q) => { acc[q.ref_no] = q; return acc; }, {});

    const responses = {};
    const errors = [];

    // ── Meta sheet (routing info) ──────────────────────────────────────────
    const meta = {};
    const metaSheet = workbook.getWorksheet('Meta');
    if (metaSheet) {
        metaSheet.eachRow((row, rowNum) => {
            if (rowNum === 1) return; // header
            const key = row.getCell(1).value;
            const val = row.getCell(2).value;
            if (key) meta[key] = val;
        });
    }

    // ── Section sheets ──────────────────────────────────────────────────────
    workbook.worksheets.forEach(sheet => {
        if (sheet.name === 'Meta') return;
        sheet.eachRow((row, rowNum) => {
            if (rowNum === 1) return; // header row
            const refNo = row.getCell(1).value;
            if (!refNo || typeof refNo !== 'string' && typeof refNo !== 'number') return;
            const refNoStr = String(refNo).trim();
            const question = questionByRefNo[refNoStr];
            if (!question) return; // group-header rows and blanks have no ref_no match

            const selectedRaw = row.getCell(5).value; // "Selected Keys" column
            const comment = row.getCell(6).value || '';

            const typed = selectedRaw ? String(selectedRaw).split(',').map(s => s.trim()).filter(Boolean) : [];
            if (typed.length === 0) return; // left blank — not answered, skip silently

            const validKeys = question.options.map(o => o.key);
            const invalid = typed.filter(k => !validKeys.includes(k));
            if (invalid.length > 0) {
                errors.push(`Row for ${refNoStr}: invalid key(s) "${invalid.join(',')}" — valid options are ${validKeys.join(',')}`);
                return; // don't apply a partially-invalid selection
            }
            if (question.question_type === 'single' && typed.length > 1) {
                errors.push(`Row for ${refNoStr}: single-select question given multiple keys "${typed.join(',')}" — skipped`);
                return;
            }

            const score = question.options
                .filter(o => typed.includes(o.key))
                .reduce((sum, o) => sum + Number(o.score), 0);

            responses[question.id] = {
                selected_options: typed,
                score,
                comment: String(comment),
            };
        });
    });

    return { responses, meta, errors };
}